import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Card, CardContent, Typography, TextField, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Chip, CircularProgress, Grid, Dialog, DialogTitle, DialogContent,
  DialogActions, InputAdornment, IconButton, Tooltip, Avatar, MenuItem,
  Switch, FormControlLabel,
} from '@mui/material';
import { Add, Refresh, Search, Edit } from '@mui/icons-material';
import { InvokeApi, apiList } from '../../services/ApiService';
import { Config } from '../../config/Config';

const emptyForm = {
  id: null,
  name: '', email: '', phone: '', password: '', role: 'Teacher',
  currentAddress: '', permanentAddress: '', status: 'Active',
};

const ROLES = ['Admin', 'Teacher'];

export default function Users() {
  const [activeUsers, setActiveUsers] = useState([]);
  const [inactiveUsers, setInactiveUsers] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showInactive, setShowInactive] = useState(true);

  const [dialog, setDialog] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState({});
  const [form, setForm] = useState(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    const [activeData, inactiveData] = await Promise.all([
      InvokeApi(Config.main_url + apiList.getUsers, {}),
      InvokeApi(Config.main_url + apiList.getUsers, { status: 'Inactive' }),
    ]);
    setActiveUsers(activeData?.users || []);
    setInactiveUsers(inactiveData?.users || []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const all = showInactive
      ? [...activeUsers, ...inactiveUsers]
      : activeUsers;
    const q = search.toLowerCase();
    setFiltered(
      all.filter(r =>
        !q ||
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.phone_number?.includes(q)
      )
    );
  }, [search, activeUsers, inactiveUsers, showInactive]);

  const handleToggle = async (user) => {
    const newStatus = user.status === 'Active' ? 'Inactive' : 'Active';
    setToggling(p => ({ ...p, [user.id]: true }));
    await InvokeApi(Config.main_url + apiList.editUser, {
      id: user.id,
      role: user.role,
      status: newStatus,
      password: user.password || '',
    });
    setToggling(p => ({ ...p, [user.id]: false }));
    load();
  };

  const openAdd = () => {
    setForm(emptyForm);
    setEditMode(false);
    setDialog(true);
  };

  const openEdit = (user) => {
    setForm({
      id: user.id,
      name: user.name || '',
      email: user.email || '',
      phone: user.phone_number || '',
      password: '',
      role: user.role || 'Teacher',
      currentAddress: user.current_address || '',
      permanentAddress: user.permanent_address || '',
      status: user.status || 'Active',
    });
    setEditMode(true);
    setDialog(true);
  };

  const handleSave = async () => {
    if (!form.name || !form.phone) {
      alert('Name and phone are required');
      return;
    }
    if (!editMode && (!form.password || !form.email)) {
      alert('Name, phone, email and password are required for new users');
      return;
    }
    setSaving(true);

    if (editMode) {
      const payload = {
        id: form.id,
        role: form.role,
        status: form.status,
      };
      if (form.password) payload.password = form.password;
      await InvokeApi(Config.main_url + apiList.editUser, payload);
    } else {
      const res = await InvokeApi(Config.main_url + apiList.addUser, {
        name: form.name,
        mobileNumber: form.phone,
        email: form.email,
        role: form.role,
        password: form.password,
        profilePath: '',
        currentAddress: form.currentAddress || '',
        permanentAddress: form.permanentAddress || '',
        createdBy: '1',
      });
      if (res?.code !== '200') {
        alert(res?.message || 'Failed to add user');
        setSaving(false);
        return;
      }
    }

    setSaving(false);
    setDialog(false);
    load();
  };

  const f = (k) => (e) => setForm(p => ({ ...p, [k]: e.target.value }));

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h5" fontWeight={800} color="#1a237e">Users / Staff</Typography>
          <Typography variant="body2" color="text.secondary">
            {activeUsers.length} active · {inactiveUsers.length} inactive
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<Add />} onClick={openAdd}
          sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700 }}>
          Add User
        </Button>
      </Box>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', mb: 3 }}>
        <CardContent sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          <TextField size="small" placeholder="Search name, email, phone..." value={search}
            onChange={e => setSearch(e.target.value)} sx={{ flex: 1, minWidth: 220 }}
            InputProps={{ startAdornment: <InputAdornment position="start"><Search fontSize="small" /></InputAdornment> }} />
          <FormControlLabel
            control={
              <Switch
                checked={showInactive}
                onChange={e => setShowInactive(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#1a237e' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#1a237e' },
                }}
              />
            }
            label={<Typography fontSize={13} fontWeight={600} color="text.secondary">Show Inactive</Typography>}
          />
          <Tooltip title="Refresh">
            <IconButton onClick={load} sx={{ color: '#1a237e', border: '1px solid #e8eaf6', borderRadius: 2 }}>
              <Refresh />
            </IconButton>
          </Tooltip>
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress sx={{ color: '#1a237e' }} />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ background: '#f5f6fa' }}>
                  {['#', 'User', 'Phone', 'Role', 'Status', 'Active/Inactive', 'Edit'].map(h => (
                    <TableCell key={h} sx={{ fontWeight: 700, color: '#1a237e', fontSize: 13, whiteSpace: 'nowrap' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      No users found
                    </TableCell>
                  </TableRow>
                ) : filtered.map((r, i) => (
                  <TableRow key={r.id || i} hover
                    sx={{ opacity: r.status === 'Inactive' ? 0.6 : 1 }}>
                    <TableCell sx={{ fontSize: 12, color: 'text.secondary' }}>{i + 1}</TableCell>

                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar src={r.profile_path} sx={{
                          width: 36, height: 36,
                          background: r.status === 'Inactive'
                            ? '#bdbdbd'
                            : 'linear-gradient(135deg,#1a237e,#1565c0)',
                          fontSize: 14, fontWeight: 700,
                        }}>
                          {r.name?.[0]}
                        </Avatar>
                        <Box>
                          <Typography fontWeight={600} fontSize={13}>{r.name}</Typography>
                          <Typography fontSize={11} color="text.secondary">{r.email || '—'}</Typography>
                        </Box>
                      </Box>
                    </TableCell>

                    <TableCell sx={{ fontSize: 12 }}>{r.phone_number || '—'}</TableCell>

                    <TableCell>
                      <Chip label={r.role || 'Teacher'} size="small"
                        sx={{ background: '#e8eaf6', color: '#1a237e', fontWeight: 600, fontSize: 11 }} />
                    </TableCell>

                    <TableCell>
                      <Chip
                        label={r.status || 'Active'}
                        size="small"
                        sx={{
                          background: r.status === 'Active' || !r.status ? '#e8f5e9' : '#ffebee',
                          color: r.status === 'Active' || !r.status ? '#2e7d32' : '#c62828',
                          fontWeight: 600, fontSize: 11,
                        }}
                      />
                    </TableCell>

                    <TableCell>
                      {toggling[r.id] ? (
                        <CircularProgress size={20} sx={{ color: '#1a237e' }} />
                      ) : (
                        <Tooltip title={r.status === 'Active' ? 'Set Inactive' : 'Set Active'}>
                          <Switch
                            checked={r.status === 'Active' || !r.status}
                            onChange={() => handleToggle(r)}
                            size="small"
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': { color: '#2e7d32' },
                              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2e7d32' },
                              '& .MuiSwitch-switchBase': { color: '#c62828' },
                              '& .MuiSwitch-track': { backgroundColor: '#ffcdd2' },
                            }}
                          />
                        </Tooltip>
                      )}
                    </TableCell>

                    <TableCell>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => openEdit(r)}
                          sx={{ color: '#1565c0', background: '#e3f2fd', borderRadius: 1.5 }}>
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Card>

      <Dialog open={dialog} onClose={() => setDialog(false)} maxWidth="sm" fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}>
        <DialogTitle sx={{ fontWeight: 700, color: '#1a237e', borderBottom: '1px solid #f0f0f0' }}>
          {editMode ? `Edit User` : 'Add New User'}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Grid container spacing={2}>
            {!editMode && (
              <>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Name *" value={form.name} onChange={f('name')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Phone *" value={form.phone} onChange={f('phone')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Email" value={form.email} onChange={f('email')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Current Address" value={form.currentAddress} onChange={f('currentAddress')} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField fullWidth size="small" label="Permanent Address" value={form.permanentAddress} onChange={f('permanentAddress')} />
                </Grid>
              </>
            )}

            {editMode && (
              <Grid item xs={12}>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  Editing: <strong>{form.name}</strong> · {form.email}
                </Typography>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField select fullWidth size="small" label="Role *" value={form.role} onChange={f('role')}>
                {ROLES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
            </Grid>

            {editMode && (
              <Grid item xs={12} sm={6}>
                <TextField select fullWidth size="small" label="Status" value={form.status} onChange={f('status')}>
                  <MenuItem value="Active">Active</MenuItem>
                  <MenuItem value="Inactive">Inactive</MenuItem>
                </TextField>
              </Grid>
            )}

            <Grid item xs={12} sm={6}>
              <TextField fullWidth size="small" label={editMode ? 'New Password (leave blank to keep)' : 'Password *'}
                type="password" value={form.password} onChange={f('password')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2.5, gap: 1, borderTop: '1px solid #f0f0f0' }}>
          <Button onClick={() => setDialog(false)} variant="outlined" sx={{ borderRadius: 2 }}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}
            sx={{ background: 'linear-gradient(135deg,#1a237e,#1565c0)', borderRadius: 2, fontWeight: 700, minWidth: 120 }}>
            {saving ? <CircularProgress size={20} color="inherit" /> : (editMode ? 'Update' : 'Add User')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
